import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import Pane from "@components/pane/pane.component";
import {useForm} from "react-hook-form";
import Form from "@components/form";
import {IonCol, IonLoading, IonRow, IonSelectOption} from "@ionic/react";
import {StyledInput} from "@components/form/input/input.style";
import {StyledContent} from "@components/accordion/accordion.style";

import {ReportApplicationApiFormat, ReportApplicationForm} from "@models/helpdesk/reportApplication";
import {
    StyledButtonWrapper,
} from "@app/travelAllowance/travelAllowance.style";
import {
    StyledButton
} from "@components/button/button.style";

import {Preferences} from "@capacitor/preferences";
import {DataSelectable} from "@models/common";
import PaginatedListModal from "@components/modal/paginatedListModal.component";
import moment, {ISO_8601, Moment} from "moment/moment";
import {
    getWorkerClients,
    getWorkerOrders,
    getWorkerReports,
    reportApplication
} from "@services/helpdesk/helpdesk.service";
import {StyledIonSelect} from "@components/form/input/select.style";
import DateInput from "@components/form/input/date.component";
import {useHistory} from "react-router-dom";
import TimeInput from "@components/form/input/time.component";
import {useToast} from "@context/toast.context";
import {StyledTextarea} from "@components/form/input/textarea.style";
import CrossImage from "@assets/images/e-remove.svg";
import {StyledReportApplicationFormContainer} from "@app/helpdesk/reportApplication/components/reportApplication.style";
import ArrowDownImage from "@assets/images/down-arrow.svg";
import {Links} from "@app/links";

type ReportApplicationPaneProps = {
    topEdge?: number;
}

const ReportApplicationPane: React.FC<ReportApplicationPaneProps> = ({topEdge}: ReportApplicationPaneProps) => {
    const {t} = useTranslation();
    const history = useHistory();

    const {register, errors, handleSubmit, setError, setValue, reset} = useForm<ReportApplicationForm>({
        mode: 'onChange'
    });

    const [showLoader, setShowLoader] = useState<boolean>(false);

    const {showToast} = useToast();

    const [selectedType, setSelectedType] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [submitted, setSubmitted] = useState(false);

    const [minDateStart, setMinDateStart] = useState<string>();
    const [isClientModalOpen, setIsClientModalOpen] = useState<boolean>(false);
    const [selectedClient, setSelectedClient] = useState<DataSelectable | undefined>(undefined);
    const handleClientSelect = (client: DataSelectable) => {
        setSelectedClient(client);
        setSelectedOrder(undefined);
        setSelectedReport(undefined);
    }

    const [isOrderModalOpen, setIsOrderModalOpen] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<DataSelectable | undefined>(undefined);
    const handleOrderSelect = (client: DataSelectable) => {
        setSelectedOrder(client);
        setSelectedReport(undefined);
    }

    const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
    const [selectedReport, setSelectedReport] = useState<DataSelectable | undefined>(undefined);
    const handleReportSelect = (client: DataSelectable) => {
        setSelectedReport(client);
    }

    const [hours, setHours] = useState<string | undefined>('');
    const [minutes, setMinutes] = useState<number>(0);
    const onHoursChange = (value: string) => {
        let hoursValue = moment(value, ISO_8601).format('HH:mm');
        const [hours, minutes] = hoursValue.split(":").map(Number);
        let finalValue = hours * 60 + minutes;
        setHours(value);
        setMinutes(finalValue);

        setValue('hours', finalValue, {shouldValidate: true});
    }

    const validate = (data: ReportApplicationForm) => {
        let isValid = true;

        if (!minutes || minutes === 0) {
            setError("hours", {type: "manual", message: "Hours required"});
            isValid = false;
        }

        if (!selectedDate) {
            setError("date", {type: "manual", message: "Date is required"});
            isValid = false;
        }

        if (data.commentWorker.trim().length < 50) {
            handleCommentError();
            isValid = false;
        }

        return isValid;
    };

    useEffect(() => {
        handleMinDateStart();
    }, []);

    const handleMinDateStart = async () => {
        const basketAvailableToDay = await Preferences.get({'key': 'worker_form_basket_available_to_day'});

        let date = moment().startOf('month').format('YYYY-MM-DD');
        if (basketAvailableToDay && basketAvailableToDay.value && Number(basketAvailableToDay.value) > moment().date()) {
            date = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
        }

        setMinDateStart(date);
    };

    const onSubmit = async (dataForm: ReportApplicationForm) => {
        setSubmitted(true);

        if (!validate(dataForm)) {
            return;
        }

        setShowLoader(true);

        const reportApplicationApiData: ReportApplicationApiFormat = {
            type: dataForm.type,
            hours: minutes,
            date: selectedDate ? selectedDate : undefined,
            client: selectedClient ? selectedClient.id : undefined,
            order: selectedOrder ? selectedOrder.id : undefined,
            report: selectedReport ? selectedReport.id : undefined,
            commentWorker: dataForm.commentWorker
        };

        await reportApplication(reportApplicationApiData)
            .then(response => {
                showToast({
                    message: t('helpdesk.reportApplication.report_sent'),
                    type: 'success',
                    duration: 6000
                })
                reset();
                setSelectedType('');
                setSelectedDate('');
                setSelectedClient(undefined);
                setSelectedOrder(undefined);
                setSelectedReport(undefined);
                setHours('');
                setMinutes(0);
                setSubmitted(false);

                setShowLoader(false);

                history.push(Links.main + Links.helpdesk.reportApplicationList);
            })
            .catch(error => {
                if (error.response.status === 400) {
                    showToast({
                        message: t('helpdesk.reportApplication.errors'),
                        type: 'danger',
                        duration: 6000
                    })
                } else {
                    showToast({
                        message: t('common.serverErrorMsg'),
                        type: 'danger',
                        duration: 6000
                    })
                }
                setShowLoader(false);
            });
    }

    const handleCommentError = () => {
        showToast({
            message: t('helpdesk.reportApplication.commentWorkerMinimum50'),
            type: 'danger',
            duration: 1000
        })
        setError("commentWorker", {
            type: "manual",
        });
    };

    return (
        <Pane topEdge={topEdge} marginTop={40} paddingBottom={147}>
            <IonLoading onDidDismiss={() => setShowLoader(false)} isOpen={showLoader}/>
            <PaginatedListModal isOpen={isClientModalOpen}
                                fetchData={getWorkerClients}
                                fetchParams={{date: selectedDate ? selectedDate : 0}}
                                setValue={handleClientSelect}
                                onClose={() => setIsClientModalOpen(false)}
            />
            <PaginatedListModal isOpen={isOrderModalOpen}
                                fetchData={getWorkerOrders}
                                fetchParams={{client: selectedClient ? selectedClient.id : 0}}
                                setValue={handleOrderSelect}
                                onClose={() => setIsOrderModalOpen(false)}
            />
            <PaginatedListModal isOpen={isReportModalOpen}
                                fetchData={getWorkerReports}
                                fetchParams={{order: selectedOrder ? selectedOrder.id : 0}}
                                setValue={handleReportSelect}
                                onClose={() => setIsReportModalOpen(false)}
            />
            <StyledContent>
                <StyledReportApplicationFormContainer>
                    <Form.Container onSubmit={handleSubmit(onSubmit)}>
                        <IonRow>
                            <IonCol size="12" className="label mt-8 required">
                                {t('helpdesk.reportApplication.type')}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label">
                                <StyledIonSelect
                                    name='type'
                                    value={selectedType}
                                    placeholder={t('common.selectPlaceholder')}
                                    okText={t('common.selectOk')}
                                    cancelText={t('common.selectCancel')}
                                    ref={register({
                                        required: true
                                    })}
                                    interfaceOptions={
                                        {
                                            header: t('helpdesk.reportApplication.type_select'),
                                            cssClass: 'alert-separators'
                                        }
                                    }
                                    className={errors.type ? 'error border padding-left' : 'border padding-left'}
                                    onIonChange={e => {
                                        setSelectedType(e.detail.value);
                                    }}>
                                    <IonSelectOption
                                        value="TOO_FEW_OR_NO_HOURS"> {t('helpdesk.reportApplication.type_too_few_or_no_hours')}</IonSelectOption>
                                    <IonSelectOption
                                        value="TOO_MANY_HOURS"> {t('helpdesk.reportApplication.type_too_many_hours')}</IonSelectOption>
                                </StyledIonSelect>
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label mt-8 required">
                                {t('helpdesk.reportApplication.hours_and_minutes')}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <input
                                type="number"
                                name="hours"
                                style={{display: 'none'}}
                                value={minutes}
                                readOnly
                                ref={register({
                                    required: true,
                                    min: {
                                        value: 1,
                                        message: t('helpdesk.reportApplication.cant_be_0')
                                    }
                                })}
                            />
                            <IonCol size="12" className="label">
                                <TimeInput
                                    keepContentsMounted={true}
                                    cancelText={t("common.dateCancel")}
                                    doneText={t("common.dateDone")}
                                    placeholder={t("helpdesk.reportApplication.hours_and_minutes_type")}
                                    onChange={e => onHoursChange(e)}
                                    className={errors.hours ? 'hasErrors' : ''}
                                    value={hours}
                                />
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label mt-8 required">
                                {t('helpdesk.reportApplication.date')}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label">
                                <DateInput
                                    presentation="date"
                                    format="DD.MM.YYYY"
                                    min={minDateStart}
                                    max={moment().format('YYYY-MM-DD')}
                                    value={selectedDate ? selectedDate : null}
                                    onChange={(value) => {
                                        setSelectedDate(moment(value).format('YYYY-MM-DD'));
                                        setSelectedClient(undefined);
                                        setSelectedOrder(undefined);
                                        setSelectedReport(undefined);
                                    }}
                                    className={errors.date ? 'hasErrors' : ''}
                                    cancelText={t("common.dateCancel")}
                                    doneText={t("common.dateDone")}
                                    placeholder={t("common.select")}
                                />
                                <input
                                    type="hidden"
                                    name="date"
                                    value={selectedDate}
                                    ref={register({
                                        required: {
                                            value: true,
                                            message: t("helpdesk.reportApplication.dateRequired")
                                        }
                                    })}
                                />
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label mt-8 required">
                                {t('helpdesk.reportApplication.client')}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="select">
                                <StyledInput readOnly={!selectedClient}
                                             name="client"
                                             ref={register({
                                                 required: true
                                             })}
                                             placeholder={t('helpdesk.reportApplication.clientPlaceholder')}
                                             className={errors.client ? 'error' : ''}
                                             value={selectedClient ? selectedClient.name : ''}
                                             onClick={() => {
                                                 if (selectedDate) {
                                                     setIsClientModalOpen(true)
                                                 }
                                             }
                                             }
                                />
                                {!selectedClient && <img className="down" src={ArrowDownImage}/>}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label mt-8">
                                {t('helpdesk.reportApplication.order')}
                            </IonCol>
                        </IonRow>

                        <IonRow>
                            <IonCol size="12" className="select">
                                <StyledInput readOnly={!selectedOrder}
                                             name="order"
                                             placeholder={t('helpdesk.reportApplication.orderPlaceholder')}
                                             value={selectedOrder ? selectedOrder.name : ''}
                                             onClick={() => {
                                                 if (selectedClient) {
                                                     setIsOrderModalOpen(true)
                                                 }
                                             }
                                             }
                                />
                                {!selectedOrder && <img className="down" src={ArrowDownImage}/>}
                                {selectedOrder && <img className="clear" src={CrossImage} onClick={(e) => {
                                    setSelectedOrder(undefined);
                                }}/>}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label mt-8">
                                {t('helpdesk.reportApplication.report')}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="select">
                                <StyledInput readOnly={!selectedReport}
                                             name="report"
                                             placeholder={t('helpdesk.reportApplication.reportPlaceholder')}
                                             value={selectedReport ? selectedReport.name : ''}
                                             onClick={() => {
                                                 if (selectedOrder) {
                                                     setIsReportModalOpen(true)
                                                 }
                                             }
                                             }
                                />
                                {!selectedReport && <img className="down" src={ArrowDownImage}/>}
                                {selectedReport && <img className="clear" src={CrossImage} onClick={(e) => {
                                    setSelectedReport(undefined);
                                }}/>}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label mt-8 required">
                                {t('helpdesk.reportApplication.commentWorker')}
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="label">
                                <StyledTextarea name='commentWorker' inputMode="text"
                                                className={errors.commentWorker ? 'error full-width resize-none small' : 'full-width resize-none small'}
                                                ref={register({
                                                    required: true,
                                                    minLength: {
                                                        value: 50,
                                                        message: t('helpdesk.reportApplication.commentWorkerMinimum50')
                                                    }
                                                })}
                                />
                                {errors.commentWorker && errors.commentWorker.message &&
                                    <p className="errorMessage">{errors.commentWorker.message}</p>}
                            </IonCol>
                        </IonRow>

                        <StyledButtonWrapper>
                            <StyledButton type="submit"
                                          className='center'>
                                <div className="btn center">
                                    <span>{t('helpdesk.reportApplication.send')}</span>
                                </div>
                            </StyledButton>
                        </StyledButtonWrapper>
                    </Form.Container>
                </StyledReportApplicationFormContainer>
            </StyledContent>
        </Pane>
    );
};

export default ReportApplicationPane;